import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useDebounced } from '@/hooks/useDebounced';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { translateApiError } from '@/lib/apiError';
import type { Patient } from '@/types/patients';
import { PatientList } from '@/features/patients/PatientList';
import { PatientSearchField } from '@/features/patients/PatientSearchField';
import { useResultNavigation } from '@/features/patients/useResultNavigation';
import { QuickCreatePatient } from '@/features/patients/QuickCreatePatient';
import { PatientHeaderBand } from '@/features/patients/PatientHeaderBand';
import { EditPatientDialog } from '@/features/patients/EditPatientDialog';
import { PatientSummaryPanel } from '@/features/patients/PatientSummaryPanel';
import { usePatient } from '@/features/patients/usePatients';
import { useStartDraft } from '@/features/patients/useConsultations';
import { features } from '@/lib/featureFlags';

const NEW = 'new';

/**
 * Phase 1 patients screen. Desktop is a two-pane master–detail (search list +
 * summary); mobile drills in (list → full-width summary with a back button and
 * a sticky Start-consultation bar). `/patients/new` renders the quick-create
 * form in the detail pane / full screen.
 */
export function PatientsPage() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 200);
  const searchRef = useRef<HTMLInputElement>(null);

  const isCreating = patientId === NEW;
  const selectedId = !isCreating ? patientId : undefined;

  // "/" focuses the search field from anywhere on the page (unless already
  // typing in a field). Mirrors the design's keyboard hint.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const el = document.activeElement;
      const typing =
        el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || (el as HTMLElement)?.isContentEditable;
      if (typing) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function selectPatient(p: Patient) {
    navigate(`/patients/${p.id}`);
  }
  function openCreate(prefill: string) {
    setQuery(prefill);
    navigate('/patients/new');
  }

  // ----- Desktop master–detail -----
  if (isDesktop) {
    return (
      <div className="mx-auto -mt-1 grid h-[calc(100vh-7rem)] w-full grid-cols-[420px_1fr] overflow-hidden rounded-lg border border-line">
        <MasterPane
          query={query}
          onQuery={setQuery}
          searchRef={searchRef}
          selectedId={selectedId}
          onSelect={selectPatient}
          onAddNew={openCreate}
        />
        <div className="min-w-0 overflow-y-auto bg-background p-6">
          {isCreating ? (
            <CreatePane initialName={query} onCreated={selectPatient} onCancel={() => navigate('/patients')} />
          ) : selectedId ? (
            <DetailPane patientId={selectedId} narrow />
          ) : (
            <DetailEmptyPane />
          )}
        </div>
      </div>
    );
  }

  // ----- Mobile -----
  if (isCreating) {
    return (
      <div className="mx-auto w-full max-w-[560px]">
        <MobileSubHeader title={t('patients.quickCreate.title')} onBack={() => navigate('/patients')} />
        <CreatePane initialName={query} onCreated={selectPatient} onCancel={() => navigate('/patients')} />
      </div>
    );
  }

  if (selectedId) {
    return <MobileDetail patientId={selectedId} onBack={() => navigate('/patients')} />;
  }

  return (
    <div className="relative mx-auto w-full max-w-[640px]">
      <header className="mb-4 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">{t('patients.title')}</h1>
        <Button variant="secondary" size="sm" onClick={() => navigate('/patients/new')}>
          <Icon name="plus" size={15} /> {t('patients.newPatient')}
        </Button>
      </header>
      <MobileSearchList
        query={query}
        onQuery={setQuery}
        debounced={debounced}
        searchRef={searchRef}
        selectedId={selectedId}
        onSelect={selectPatient}
        onAddNew={openCreate}
      />
      <button
        type="button"
        aria-label={t('patients.newPatient')}
        onClick={() => navigate('/patients/new')}
        className="fixed bottom-24 right-5 z-20 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg"
      >
        <Icon name="plus" size={24} strokeWidth={2} />
      </button>
    </div>
  );
}

// ---------- Mobile search + list ----------

interface MobileSearchListProps {
  query: string;
  onQuery: (q: string) => void;
  debounced: string;
  searchRef: React.RefObject<HTMLInputElement>;
  selectedId?: string;
  onSelect: (p: Patient) => void;
  onAddNew: (prefill: string) => void;
}

function MobileSearchList({
  query,
  onQuery,
  debounced,
  searchRef,
  selectedId,
  onSelect,
  onAddNew,
}: MobileSearchListProps) {
  const { highlightedId, activeDescendantId, onKeyDown, setPatients } = useResultNavigation(onSelect);
  return (
    // Wrap input + list together so ↑/↓/Enter from the focused input reach the
    // keydown handler that drives the highlight.
    <div onKeyDown={onKeyDown}>
      <div className="sticky top-0 z-10 -mx-5 bg-background px-5 pb-3 pt-1">
        <PatientSearchField
          inputRef={searchRef}
          value={query}
          onChange={onQuery}
          autoFocus
          activeDescendantId={activeDescendantId}
        />
      </div>
      <PatientList
        query={debounced}
        selectedId={selectedId}
        onSelect={onSelect}
        onAddNew={onAddNew}
        variant="card"
        highlightedId={highlightedId}
        onResultsChange={setPatients}
      />
    </div>
  );
}

// ---------- Desktop master pane ----------

interface MasterPaneProps {
  query: string;
  onQuery: (q: string) => void;
  searchRef: React.RefObject<HTMLInputElement>;
  selectedId?: string;
  onSelect: (p: Patient) => void;
  onAddNew: (prefill: string) => void;
}

function MasterPane({ query, onQuery, searchRef, selectedId, onSelect, onAddNew }: MasterPaneProps) {
  const { t } = useTranslation();
  const debounced = useDebounced(query.trim(), 200);
  const { highlightedId, activeDescendantId, onKeyDown, setPatients } = useResultNavigation(onSelect);
  return (
    // The keydown handler lives on the wrapper that contains BOTH the search
    // input and the list, so ↑/↓/Enter typed in the focused input reach it.
    <div className="flex min-h-0 flex-col border-r border-line bg-paper" onKeyDown={onKeyDown}>
      <div className="shrink-0 p-4">
        <PatientSearchField
          inputRef={searchRef}
          value={query}
          onChange={onQuery}
          autoFocus
          size="md"
          activeDescendantId={activeDescendantId}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
        <PatientList
          query={debounced}
          selectedId={selectedId}
          onSelect={onSelect}
          onAddNew={onAddNew}
          variant="row"
          highlightedId={highlightedId}
          onResultsChange={setPatients}
        />
      </div>
      <div className="flex shrink-0 items-center gap-3.5 border-t border-line px-4 py-2.5 text-[11.5px] text-muted-2">
        <KbdHint keys={['/']} label={t('patients.kbdSearch')} />
        <KbdHint keys={['↑', '↓']} label={t('patients.kbdMove')} />
        <KbdHint keys={['⏎']} label={t('patients.kbdOpen')} />
      </div>
    </div>
  );
}

// ---------- Detail panes ----------

function DetailPane({ patientId, narrow }: { patientId: string; narrow?: boolean }) {
  const { data: patient } = usePatient(patientId);
  const { startConsultation, disabled, error } = useStartConsultation(patientId);
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex flex-col gap-6">
      {patient && (
        <PatientHeaderBand
          patient={patient}
          onEdit={() => setEditing(true)}
          onStartConsultation={
            features.doctorConsultEntry ? () => void startConsultation() : undefined
          }
          canDelete={false}
          startDisabled={disabled}
        />
      )}
      {error && <StartConsultError message={error} />}
      <PatientSummaryPanel patientId={patientId} narrow={narrow} />
      {editing && patient && (
        <EditPatientDialog patient={patient} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

function MobileDetail({ patientId, onBack }: { patientId: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { data: patient } = usePatient(patientId);
  const { startConsultation, disabled, error } = useStartConsultation(patientId);
  const [editing, setEditing] = useState(false);
  return (
    <div
      className={
        'relative mx-auto w-full max-w-[640px]' + (features.doctorConsultEntry ? ' pb-24' : '')
      }
    >
      <MobileSubHeader title={patient?.fullName ?? t('patients.title')} onBack={onBack} />
      {patient && (
        <PatientHeaderBand
          patient={patient}
          onEdit={() => setEditing(true)}
          onStartConsultation={
            features.doctorConsultEntry ? () => void startConsultation() : undefined
          }
          canDelete={false}
        />
      )}
      {editing && patient && (
        <EditPatientDialog patient={patient} onClose={() => setEditing(false)} />
      )}
      {error && (
        <div className="mt-3">
          <StartConsultError message={error} />
        </div>
      )}
      <div className="mt-5">
        <PatientSummaryPanel patientId={patientId} />
      </div>
      {/* Sticky bottom Start-consultation bar. */}
      {features.doctorConsultEntry && (
        <div className="fixed inset-x-0 bottom-[72px] z-20 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur md:hidden">
          <Button
            full
            size="lg"
            onClick={() => void startConsultation()}
            disabled={disabled}
            aria-label={t('patients.startConsultation')}
          >
            <Icon name="arrow_up_right" size={18} /> {t('patients.startConsultation')}
          </Button>
        </div>
      )}
    </div>
  );
}

function CreatePane({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName: string;
  onCreated: (p: Patient) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold tracking-[-0.02em]">{t('patients.quickCreate.title')}</h2>
        <p className="mt-1 text-[13px] text-muted">{t('patients.quickCreate.subtitle')}</p>
      </div>
      <QuickCreatePatient initialName={initialName} onCreated={onCreated} onCancel={onCancel} />
    </div>
  );
}

function DetailEmptyPane() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center px-10 text-center">
      <div className="mb-5 flex h-[76px] w-[76px] items-center justify-center rounded-full border border-line bg-paper-2 text-muted-2">
        <Icon name="user" size={32} strokeWidth={1.4} />
      </div>
      <div className="text-lg font-semibold tracking-[-0.02em]">{t('patients.selectPatientTitle')}</div>
      <p className="mt-2 max-w-[320px] text-[13.5px] leading-relaxed text-muted">
        {t('patients.selectPatientBody')}
      </p>
      <div className="mt-5 flex items-center gap-3.5 text-[12px] text-muted-2">
        <KbdHint keys={['↑', '↓']} label={t('patients.kbdNavigate')} />
        <KbdHint keys={['⏎']} label={t('patients.kbdOpen')} />
      </div>
    </div>
  );
}

// ---------- Start-consultation handling ----------

/**
 * Wraps `useStartDraft`: starts a fresh consultation draft for the patient and
 * navigates into the Phase 2 consultation form. Disabled only while the start
 * mutation is in flight.
 */
function useStartConsultation(patientId: string) {
  const navigate = useNavigate();
  const startDraft = useStartDraft(patientId);
  const [error, setError] = useState<string | null>(null);

  async function startConsultation() {
    if (startDraft.isPending) return;
    setError(null);
    try {
      const c = await startDraft.mutateAsync({});
      navigate(`/patients/${patientId}/consult/${c.id}`);
    } catch (err) {
      // Surface — a failed start (network/permission/500) must not be silent.
      setError(translateApiError(err));
    }
  }

  return { startConsultation, disabled: startDraft.isPending, error };
}

/** Inline error shown beneath the Start-consultation control when a start fails. */
function StartConsultError({ message }: { message: string }) {
  return (
    <div
      className="flex items-center gap-2 rounded-md border border-destructive bg-destructive-bg px-3 py-2 text-[12.5px] text-destructive"
      role="alert"
    >
      <Icon name="alert" size={14} className="shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ---------- Small shared bits ----------

function MobileSubHeader({ title, onBack }: { title: string; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex items-center gap-2">
      <button
        type="button"
        onClick={onBack}
        aria-label={t('patients.backToPatients')}
        className="flex h-9 w-9 items-center justify-center rounded-md text-ink hover:bg-paper-2"
      >
        <Icon name="back" size={18} />
      </button>
      <h1 className="min-w-0 flex-1 truncate text-lg font-semibold tracking-[-0.02em]">{title}</h1>
    </div>
  );
}

function KbdHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {keys.map((k) => (
        <kbd
          key={k}
          className="inline-flex h-5 min-w-[19px] items-center justify-center rounded border border-line-2 border-b-2 bg-paper px-1 font-mono text-[11px] text-muted"
        >
          {k}
        </kbd>
      ))}
      {label}
    </span>
  );
}
